/**
 * @fileoverview Dynamic Permissions Loader
 * Fetches role permissions from backend API and caches them
 */

import { API_ENDPOINTS } from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

// In-memory cache
let _permissionsCache = null;
let _cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch permissions from backend API
 * @returns {Promise<Object>} Role permissions mapping
 */
export async function fetchPermissionsFromAPI() {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (!token) {
      console.warn('[DYNAMIC PERMISSIONS] No auth token available');
      return null;
    }
    
    // Use authenticated endpoint available to all users
    const response = await fetch(`${API_ENDPOINTS.API_BASE_URL}/api/role-permissions`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (!response.ok) {
      console.warn('[DYNAMIC PERMISSIONS] API returned', response.status);
      return null;
    }
    
    const data = await response.json();
    
    // Transform backend format to frontend format
    const transformed = {};
    for (const [role, perms] of Object.entries(data.permissions)) {
      transformed[role] = {
        features: perms.features || [],
        canViewFinancials: perms.can_view_financials || false,
        filterByAssignedClients: perms.filter_by_assigned_clients || false,
        filterUnsignedOnly: perms.filter_unsigned_only || false,
        canManageRoles: role === 'admin'
      };
    }
    
    console.log('[DYNAMIC PERMISSIONS] Loaded from API:', Object.keys(transformed).length, 'roles');
    return transformed;
    
  } catch (error) {
    console.error('[DYNAMIC PERMISSIONS] Error fetching:', error);
    return null;
  }
}

/**
 * Get role permissions with caching
 * @returns {Promise<Object|null>} Role permissions mapping or null if not available
 */
export async function getDynamicPermissions() {
  // Check cache
  const now = Date.now();
  if (_permissionsCache && _cacheTimestamp && (now - _cacheTimestamp < CACHE_TTL)) {
    return _permissionsCache;
  }
  
  // Fetch fresh data
  const permissions = await fetchPermissionsFromAPI();
  if (permissions) {
    _permissionsCache = permissions;
    _cacheTimestamp = now;
  }
  
  return permissions;
}

/**
 * Clear the permissions cache (call after updates)
 */
export function clearDynamicPermissionsCache() {
  _permissionsCache = null;
  _cacheTimestamp = null;
  console.log('[DYNAMIC PERMISSIONS] Cache cleared');
}

/**
 * Initialize permissions cache (call on app load)
 */
export async function initializePermissionsCache() {
  console.log('[DYNAMIC PERMISSIONS] Initializing cache...');
  await getDynamicPermissions();
}

